import { useCallback, useEffect, useState } from 'react';
import { addPlatform, addStay, listPlatforms, listStays, removeStay } from '../../services/schedule';
import './schedule.css';
import './compact-schedule.css';

const formatDate = (value) => new Date(`${value}T12:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
const initial = { guest_name: '', guest_email: '', guest_phone: '', check_in: '', check_out: '', room_number: '', booked_room_name: '', platform_name: 'In house' };

export default function SchedulePanel({ rooms }) {
  const [stays, setStays] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [draft, setDraft] = useState(initial);
  const [newPlatform, setNewPlatform] = useState('');
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [guestOpen, setGuestOpen] = useState(false);
  const [roomFilter, setRoomFilter] = useState('all');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const reload = useCallback(async () => {
    const [nextStays, nextPlatforms] = await Promise.all([listStays(), listPlatforms()]);
    setStays(nextStays);
    setPlatforms(nextPlatforms);
  }, []);

  useEffect(() => {
    reload().catch((error) => setNotice(error.message));
  }, [reload]);

  useEffect(() => {
    if (!guestOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setGuestOpen(false);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [guestOpen]);

  const change = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const days = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate();
  const today = new Date().toISOString().slice(0, 10);
  const year = Number(month.slice(0, 4));
  const monthIndex = Number(month.slice(5, 7)) - 1;
  const firstDay = new Date(year, monthIndex, 1).getDay();
  const monthName = new Date(year, monthIndex, 1).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const selectedRooms = rooms.filter((room) => roomFilter === 'all' || String(room.number) === roomFilter);
  const selectedStays = stays.filter((stay) =>
    (roomFilter === 'all' || String(stay.room_number) === roomFilter) &&
    (platformFilter === 'all' || stay.platform_name === platformFilter)
  );

  function moveMonth(offset) {
    const next = new Date(year, monthIndex + offset, 1);
    setMonth(`${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`);
  }

  async function save(event) {
    event.preventDefault();
    if (draft.check_out <= draft.check_in) return setNotice('Check-out must be after check-in.');
    setBusy(true);
    setNotice('');
    try {
      await addStay({ ...draft, room_number: Number(draft.room_number) });
      await reload();
      setDraft(initial);
      setGuestOpen(false);
      setNotice('Stay added to the room schedule.');
    } catch (error) {
      setNotice(error.message);
    } finally {
      setBusy(false);
    }
  }

  async function createPlatform(event) {
    event.preventDefault();
    if (!newPlatform.trim()) return;
    try {
      await addPlatform(newPlatform);
      await reload();
      setDraft((current) => ({ ...current, platform_name: newPlatform.trim() }));
      setNewPlatform('');
      setNotice('Platform added.');
    } catch (error) {
      setNotice(error.message);
    }
  }

  async function clearStay(stay) {
    if (!window.confirm(`Remove the scheduled stay for ${stay.guest_name}?`)) return;
    try {
      await removeStay(stay.id);
      await reload();
      setNotice('Scheduled stay removed.');
    } catch (error) {
      setNotice(error.message);
    }
  }

  return (
    <div className="glass-stack schedule-panel">
      <div className="glass-card">
        <div className="compact-schedule-heading">
          <div>
            <span className="eyebrow">Room management</span>
            <h2 className="glass-title">Stay calendar</h2>
            <p className="muted">Only stays entered here block public availability. Check-out days remain available for new arrivals.</p>
          </div>
          <button className="button button-gold" type="button" onClick={() => { setNotice(''); setGuestOpen(true); }}>+ Add guest</button>
        </div>
        <div className="compact-schedule-toolbar">
          <div className="compact-schedule-navigation">
            <button type="button" aria-label="Previous month" onClick={() => moveMonth(-1)}>‹</button>
            <strong>{monthName}</strong>
            <button type="button" aria-label="Next month" onClick={() => moveMonth(1)}>›</button>
            <button type="button" onClick={() => setMonth(today.slice(0, 7))}>Today</button>
          </div>
          <div className="compact-schedule-filters">
            <label>Room <select value={roomFilter} onChange={(event) => setRoomFilter(event.target.value)}><option value="all">All rooms</option>{rooms.map((room) => <option key={room.number} value={room.number}>{room.title}</option>)}</select></label>
            <label>Source <select value={platformFilter} onChange={(event) => setPlatformFilter(event.target.value)}><option value="all">All sources</option>{platforms.map((item) => <option key={item.name} value={item.name}>{item.name}</option>)}</select></label>
            <label>Status <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}><option value="all">All dates</option><option value="available">Available</option><option value="booked">Booked</option></select></label>
          </div>
        </div>
        <div className="compact-schedule-overflow">
          <div className="compact-schedule-calendar" aria-label={`Room availability for ${monthName}`}>
            <div className="compact-schedule-weekdays">{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => <span key={day}>{day}</span>)}</div>
            <div className="compact-schedule-grid">
              {Array.from({ length: firstDay }, (_, index) => <div className="compact-schedule-blank" key={`blank-${index}`} aria-hidden="true" />)}
              {Array.from({ length: days }, (_, index) => {
                const date = `${month}-${String(index + 1).padStart(2, '0')}`;
                const statuses = selectedRooms.map((room) => ({
                  room,
                  stay: stays.find((item) => item.room_number === room.number && item.check_in <= date && item.check_out > date)
                })).filter(({ stay }) => statusFilter === 'all' || (statusFilter === 'booked' ? Boolean(stay) : !stay))
                  .filter(({ stay }) => platformFilter === 'all' || (stay && stay.platform_name === platformFilter));
                return (
                  <div className={`compact-schedule-date${date === today ? ' is-today' : ''}`} key={date}>
                    <span className="compact-schedule-number">{index + 1}</span>
                    {statuses.map(({ room, stay }) => (
                      <div key={room.number} className={`compact-schedule-room ${stay ? 'is-booked' : 'is-available'}`} title={stay ? `${room.title}: ${stay.guest_name} · ${stay.platform_name}` : `${room.title}: available ${date}`}>
                        <i aria-hidden="true" /><span>{room.title}</span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <p className="muted">Red = booked · Green = available</p>
      </div>
      {guestOpen && (
        <div className="compact-schedule-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setGuestOpen(false); }}>
          <div className="compact-schedule-dialog" role="dialog" aria-modal="true" aria-labelledby="add-stay-title">
            <form className="glass-card schedule-form" onSubmit={save}>
              <div className="compact-schedule-dialog-heading">
                <h2 className="glass-title" id="add-stay-title">Add a stay</h2>
                <button type="button" aria-label="Close add guest" onClick={() => setGuestOpen(false)}>×</button>
              </div>
              <div className="schedule-fields">
                <label className="form-field"><span>Guest name</span><input required value={draft.guest_name} onChange={(event) => change('guest_name', event.target.value)} /></label>
                <label className="form-field"><span>Guest email</span><input type="email" required value={draft.guest_email} onChange={(event) => change('guest_email', event.target.value)} /></label>
                <label className="form-field"><span>Contact number</span><input type="tel" required value={draft.guest_phone} onChange={(event) => change('guest_phone', event.target.value)} /></label>
                <label className="form-field"><span>Booked room name</span><input required value={draft.booked_room_name} onChange={(event) => change('booked_room_name', event.target.value)} /></label>
                <label className="form-field"><span>Room checked into</span><select required value={draft.room_number} onChange={(event) => change('room_number', event.target.value)}><option value="">Choose a room</option>{rooms.map((room) => <option key={room.number} value={room.number}>{room.title}</option>)}</select></label>
                <label className="form-field"><span>Booking platform</span><select required value={draft.platform_name} onChange={(event) => change('platform_name', event.target.value)}>{platforms.length ? platforms.map((item) => <option key={item.name}>{item.name}</option>) : <option>In house</option>}</select></label>
                <label className="form-field"><span>Check-in</span><input type="date" required value={draft.check_in} onChange={(event) => change('check_in', event.target.value)} /></label>
                <label className="form-field"><span>Check-out</span><input type="date" required min={draft.check_in || undefined} value={draft.check_out} onChange={(event) => change('check_out', event.target.value)} /></label>
              </div>
              <button className="button button-gold" disabled={busy} type="submit">{busy ? 'Saving…' : 'Add to schedule'}</button>
              {notice && <p role="status" className="form-status">{notice}</p>}
            </form>
          </div>
        </div>
      )}
      {!guestOpen && notice && <p role="status" className="form-status">{notice}</p>}
      <form className="glass-card schedule-platform" onSubmit={createPlatform}>
        <h2 className="glass-title">Booking platforms</h2>
        <p className="muted">Add another source to the booking platform list.</p>
        <div className="schedule-inline"><input aria-label="New platform name" maxLength={80} placeholder="Platform name" value={newPlatform} onChange={(event) => setNewPlatform(event.target.value)} required /><button className="button button-outline" type="submit">Add platform</button></div>
      </form>
      <div className="glass-card">
        <h2 className="glass-title">Scheduled stays</h2>
        {!stays.length && <p className="muted">No stays scheduled yet.</p>}
        <div className="schedule-stays">{selectedStays.map((stay) => (
          <div className="schedule-stay" key={stay.id}>
            <div><strong>{stay.guest_name}</strong><small>{rooms.find((room) => room.number === stay.room_number)?.title || `Room ${stay.room_number}`} · {formatDate(stay.check_in)} → {formatDate(stay.check_out)} · {stay.platform_name}</small><small>Booked as {stay.booked_room_name} · {stay.guest_email} · {stay.guest_phone}</small></div>
            <button type="button" className="admin-remove" onClick={() => clearStay(stay)}>Remove</button>
          </div>
        ))}</div>
      </div>
    </div>
  );
}